/**
 * useUserFieldOptions — the options the SERVER says a user field may hold.
 *
 * Foundations 2 (Users rail), B2c-T2. principles.md #3: "nothing that
 * describes a noun is hardcoded". The browser is told what a field's options
 * are; it never declares them.
 *
 * ── WHAT THIS REPLACES ────────────────────────────────────────────────────
 *
 * The employment-type options were written into the browser three times over:
 *
 *   - `create-user-dialog.tsx` — a RadioGroup over the literal array
 *     `['fte', 'contractor', 'vendor', 'partner']`;
 *   - `edit-user-dialog-v2.tsx` — four hand-written `<SelectItem>`s with the
 *     same four values and their own labels;
 *   - `routes/users/index.tsx` — the roster's employment filter, a fifth
 *     hand-written list, plus the URL search schema's own copy.
 *
 * None of those four values matched the store, which held
 * `full_time | part_time | contractor | intern`, so each dialog also carried a
 * translation map — and neither map was injective. "Vendor" was stored as
 * `contractor` and redrawn as "Contractor"; `intern` was a value no screen
 * could name.
 *
 * c32 made it one alphabet of six (`@argusai/shared` `EmploymentTypeSchema`,
 * and the `users_employment_type_check` CHECK is that list). This hook fetches
 * it. The value a control carries IS the value the store holds.
 *
 * ── FAIL CLOSED, AND SAY SO ───────────────────────────────────────────────
 *
 * Before the answer arrives, `employmentTypes` is EMPTY — not a guessed list.
 * A control with no options renders no options, which is visibly "we have not
 * been told yet" rather than a value that might not be storable
 * (principles.md #4).
 *
 * That is only half of it. Reviews B2cT2-R-02 / BF-03 / BF-08: this header
 * used to say "callers gate on `isLoading`" while every one of the eight call
 * sites destructured `{ options }` and dropped `isLoading` and `error`, so a
 * failed or slow `GET /users/field-options` was SILENT. The create wizard's
 * step 1 requires an employment type and the RadioGroup renders the served
 * options, so an empty answer left Next permanently disabled with nothing on
 * screen explaining why — user creation blocked outright. And the absent
 * answer's `requiresEndDate` is `false`, which is the PERMISSIVE direction: an
 * existing contractor's End date silently re-labelled itself "(optional)" and
 * dropped the amber warning.
 *
 * So the absence is now nameable: `isAvailable` is false until the server has
 * answered, and `refetch` lets the operator ask again. Every screen that
 * renders an option or applies the end-date rule checks it and shows
 * `<FieldOptionsNotice>`; `requiresEndDate` stays `false` on absence only
 * because no caller is allowed to believe it while `isAvailable` is false.
 *
 * Backend: GET /api/v1/users/field-options.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import {
  type EmploymentType,
  type EmploymentTypeOption,
  type UserFieldOptionsResponse,
} from '@argusai/shared/schemas/user.schema';

export type { EmploymentTypeOption };

export interface UserFieldOptionsView {
  /**
   * Has the server answered? FALSE means every other member of this view is
   * an absence, not a fact — `employmentTypes` is empty because we were not
   * told, and `requiresEndDate` answers `false` because it has nothing to
   * read. A caller that renders an option or applies the end-date rule must
   * check this first (reviews B2cT2-R-02 / BF-03 / BF-08).
   */
  isAvailable: boolean;
  /** The employment types this deployment stores, in declaration order. */
  employmentTypes: EmploymentTypeOption[];
  /** Does this option's engagement need an end date on the form? */
  requiresEndDate: (value: EmploymentType | null | undefined) => boolean;
  /** The label to show for a stored value; `null` for one we were not told about. */
  labelFor: (value: EmploymentType | null | undefined) => string | null;
}

/**
 * The empty answer. Not a default list — an absence.
 *
 * Exported so the guard beside this file can assert the absence names itself
 * (`isAvailable: false`) rather than reading it out of the source text.
 */
export const NO_USER_FIELD_OPTIONS: UserFieldOptionsView = {
  isAvailable: false,
  employmentTypes: [],
  requiresEndDate: () => false,
  labelFor: () => null,
};

export function useUserFieldOptions(): {
  options: UserFieldOptionsView;
  isLoading: boolean;
  error: unknown;
  /** Ask again — the Retry the notice offers. */
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery<UserFieldOptionsResponse>({
    queryKey: ['user-field-options'],
    queryFn: () =>
      apiClient.get<UserFieldOptionsResponse>('/users/field-options'),
    // The field's own alphabet. It changes when the platform ships a
    // migration, not while a tab is open.
    staleTime: 60 * 60_000,
  });

  const retry = () => {
    void refetch();
  };

  if (!data) return { options: NO_USER_FIELD_OPTIONS, isLoading, error, refetch: retry };

  const byValue = new Map(data.employmentTypes.map((o) => [o.value, o]));

  const options: UserFieldOptionsView = {
    isAvailable: true,
    employmentTypes: data.employmentTypes,
    requiresEndDate: (value) =>
      value ? byValue.get(value)?.requiresEndDate === true : false,
    labelFor: (value) => (value ? byValue.get(value)?.label ?? null : null),
  };

  return { options, isLoading, error, refetch: retry };
}
